import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box, Button, Alert, Dialog, DialogTitle, DialogContent, DialogActions,
  Stepper, Step, StepLabel, Typography, Chip, Divider, List, ListItem, ListItemText,
  ListItemSecondaryAction, IconButton, Tooltip, Tabs, Tab, Paper,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RouteIcon from '@mui/icons-material/Route';
import ChatIcon from '@mui/icons-material/Chat';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PlaceIcon from '@mui/icons-material/Place';
import { PageHeader } from '../../components/ui/PageHeader';
import { getPageSubtitle } from '../../constants/pageSubtitles';
import { useSystemTotal } from '../../hooks/useSystemTotal';
import { DataTable } from '../../components/common/DataTable';
import { RecordDetailDrawer } from '../../components/common/RecordDetailDrawer';
import { StylishFormModal } from '../../components/common/StylishFormModal';
import { SearchableEntitySelect, EntityOption } from '../../components/common/SearchableEntitySelect';
import { tenantModules } from '../../config/modules';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useLocale } from '../../contexts/LocaleContext';
import { TripStopsEditor, TripStopRow } from '../../components/trips/TripStopsEditor';
import { MULTI_STOP_TYPES } from '../../constants/tripTypes';
import api, {
  tripAPI, clientAPI, branchAPI, driverAPI, vehicleAPI, helperAPI, recordAPI,
} from '../../services/api';
import { brandColors, RADIUS, brandCss } from '../../theme/appTheme';
import { useTableColumns } from '../../hooks/useTableColumns';
import { DEFAULT_ROWS_PER_PAGE } from '../../constants/tableDefaults';
import { computeDrivingRoute, tripRoutePoints } from '../../components/gps/googleRoute';

const tripConfig = tenantModules.find((m) => m.key === 'trips')!;
const WORKFLOW_STEPS = ['created', 'assigned', 'in_progress', 'completed', 'approved'];
const TRIP_FORM_EXCLUDE = new Set(['actualStartTime', 'actualEndTime', 'status', 'leadName']);

const statusLabel = (s: string) => {
  const map: Record<string, string> = {
    created: 'New',
    assigned: 'Assigned',
    in_progress: 'Ongoing',
    completed: 'Completed',
    approved: 'Approved',
    cancelled: 'Cancelled',
    pending: 'New',
  };
  return map[s] || s;
};

type TripView = 'all' | 'ongoing' | 'completed' | 'cancelled';

const TRIP_VIEWS: { key: TripView; label: string; match: (s: string) => boolean }[] = [
  { key: 'all', label: 'All Trips', match: () => true },
  {
    key: 'ongoing',
    label: 'Ongoing',
    match: (s) => ['created', 'pending', 'assigned', 'in_progress'].includes(s),
  },
  {
    key: 'completed',
    label: 'Completed',
    match: (s) => ['completed', 'approved'].includes(s),
  },
  {
    key: 'cancelled',
    label: 'Cancelled',
    match: (s) => s === 'cancelled',
  },
];

const tripStatusOf = (row: Record<string, unknown>) =>
  String(row.status || 'created').toLowerCase() === 'pending'
    ? 'created'
    : String(row.status || 'created').toLowerCase();

const activeStepIndex = (status: string) => {
  const normalized = status === 'pending' ? 'created' : status;
  const idx = WORKFLOW_STEPS.indexOf(normalized);
  return idx >= 0 ? idx : 0;
};

export const TripsListPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const { formatDate } = useLocale();
  const [rows, setRows] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [formStops, setFormStops] = useState<TripStopRow[]>([]);
  const [routeLoading, setRouteLoading] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignData, setAssignData] = useState({ driver: '', vehicle: '', helper: '' });
  const [clientOptions, setClientOptions] = useState<EntityOption[]>([]);
  const [branchOptions, setBranchOptions] = useState<EntityOption[]>([]);
  const [driverOptions, setDriverOptions] = useState<EntityOption[]>([]);
  const [vehicleOptions, setVehicleOptions] = useState<EntityOption[]>([]);
  const [helperOptions, setHelperOptions] = useState<EntityOption[]>([]);
  const [leadPrefillId, setLeadPrefillId] = useState<string | null>(null);
  const { total: systemTotal, refresh: refreshTotal } = useSystemTotal('trips');
  const [tripView, setTripView] = useState<TripView>(() => {
    const v = searchParams.get('view');
    if (v === 'ongoing' || v === 'completed' || v === 'cancelled') return v;
    return 'all';
  });

  const canCreate = hasPermission('trips', 'create');
  const canView = hasPermission('trips', 'view');
  const canEdit = hasPermission('trips', 'edit');
  const canDelete = hasPermission('trips', 'delete');
  const canApprove = hasPermission('trips', 'approve');
  const canImport = hasPermission('trips', 'import');
  const canExport = hasPermission('trips', 'export');
  const tableColumns = useTableColumns('trips', tripConfig.columns);

  useEffect(() => {
    const v = searchParams.get('view');
    if (v === 'ongoing' || v === 'completed' || v === 'cancelled' || v === 'all') {
      setTripView(v === 'all' ? 'all' : v);
      setPage(0);
    }
  }, [searchParams]);

  const viewCounts = useMemo(() => {
    const counts: Record<TripView, number> = { all: rows.length, ongoing: 0, completed: 0, cancelled: 0 };
    rows.forEach((row) => {
      const s = tripStatusOf(row);
      TRIP_VIEWS.forEach((view) => {
        if (view.key !== 'all' && view.match(s)) counts[view.key] += 1;
      });
    });
    return counts;
  }, [rows]);

  const filteredRows = useMemo(() => {
    const view = TRIP_VIEWS.find((v) => v.key === tripView) || TRIP_VIEWS[0];
    return rows.filter((row) => view.match(tripStatusOf(row)));
  }, [rows, tripView]);

  const fetchRows = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const res = await tripAPI.getAll();
      setRows(res.data);
      refreshTotal();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Failed to load trips.');
    } finally {
      setLoading(false);
    }
  }, [refreshTotal]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  useEffect(() => {
    const leadId = searchParams.get('leadId');
    if (!leadId || !canCreate) return;
    recordAPI.get('leads', leadId).then((res) => {
      const data = res.data?.data || {};
      setLeadPrefillId(leadId);
      setFormData((prev) => ({
        ...prev,
        tripNumber: `TRP-L${Date.now().toString().slice(-6)}`,
        source: String(data.company || data.name || ''),
        destination: 'TBD',
        leadName: String(data.name || ''),
        notifyWhatsApp: 'true',
      }));
      setOpen(true);
      setSuccess(`Lead "${data.name || 'record'}" loaded — complete trip details and save`);
    }).catch(() => {});
  }, [searchParams, canCreate]);

  useEffect(() => {
    Promise.all([
      clientAPI.getAll(),
      branchAPI.getAll(),
      driverAPI.getAll(),
      vehicleAPI.getAll(),
      helperAPI.getAll(),
    ]).then(([clients, branches, drivers, vehicles, helpers]) => {
      setClientOptions((clients.data || []).map((c: Record<string, string>) => ({
        _id: String(c._id),
        label: c.clientName || c.companyFullName || 'Client',
        sublabel: c.companyFullName,
      })));
      setBranchOptions((branches.data || []).map((b: Record<string, string>) => ({
        _id: String(b._id),
        label: b.branchName || 'Branch',
        sublabel: b.branchCode,
      })));
      setDriverOptions((drivers.data || []).map((d: Record<string, string>) => ({
        _id: String(d._id),
        label: d.driverName || 'Driver',
        sublabel: d.driverCode,
      })));
      setVehicleOptions((vehicles.data || []).map((v: Record<string, string>) => ({
        _id: String(v._id),
        label: v.vehicleNumber || 'Vehicle',
        sublabel: v.vehicleType,
      })));
      setHelperOptions((helpers.data || []).map((h: Record<string, string>) => ({
        _id: String(h._id),
        label: h.helperName || 'Helper',
        sublabel: h.helperCode,
      })));
    }).catch(() => {});
  }, []);

  const resetForm = () => {
    setFormData({
      tripNumber: `TRP-${Date.now().toString().slice(-8)}`,
      tripType: 'one_way',
      source: '',
      destination: '',
      startDate: new Date().toISOString().slice(0, 16),
      endDate: '',
      returnSource: '',
      returnDestination: '',
      googleMapRoute: '',
      distance: '',
      eta: '',
      client: '',
      branch: '',
      driver: '',
      helper: '',
      vehicle: '',
      notifyWhatsApp: 'true',
      recurrenceEndDate: '',
      recurrenceDays: '1,2,3,4,5',
      leadName: '',
    });
    setFormStops([]);
    setEditId(null);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (row: Record<string, unknown>) => {
    setEditId(String(row._id));
    setFormData({
      tripNumber: String(row.tripNumber || ''),
      tripType: String(row.tripType || 'one_way'),
      source: String(row.source || ''),
      destination: String(row.destination || ''),
      startDate: row.startDate ? new Date(String(row.startDate)).toISOString().slice(0, 16) : '',
      endDate: row.endDate ? new Date(String(row.endDate)).toISOString().slice(0, 16) : '',
      returnSource: String(row.returnSource || ''),
      returnDestination: String(row.returnDestination || ''),
      googleMapRoute: String(row.googleMapRoute || ''),
      distance: row.distance != null ? String(row.distance) : '',
      eta: String(row.eta || ''),
      client: String((row.client as { _id?: string })?._id || row.client || ''),
      branch: String((row.branch as { _id?: string })?._id || row.branch || ''),
      driver: String((row.driver as { _id?: string })?._id || row.driver || ''),
      helper: String((row.helper as { _id?: string })?._id || row.helper || ''),
      vehicle: String((row.vehicle as { _id?: string })?._id || row.vehicle || ''),
      notifyWhatsApp: 'true',
      recurrenceEndDate: row.recurrenceEndDate ? new Date(String(row.recurrenceEndDate)).toISOString().slice(0, 10) : '',
      recurrenceDays: Array.isArray(row.recurrenceDays) ? (row.recurrenceDays as number[]).join(',') : '1,2,3,4,5',
      leadName: String(row.leadName || ''),
    });
    const rawStops = row.stops as TripStopRow[] | undefined;
    setFormStops((rawStops || []).map((s, i) => ({ location: s.location, sequence: s.sequence ?? i + 1 })));
    setOpen(true);
  };

  const buildPayload = () => {
    const tripType = formData.tripType || 'one_way';
    const payload: Record<string, unknown> = {
      tripNumber: formData.tripNumber,
      client: formData.client,
      branch: formData.branch,
      tripType,
      source: formData.source,
      destination: formData.destination,
      startDate: formData.startDate ? new Date(formData.startDate).toISOString() : undefined,
    };
    if (tripType === 'round_trip') {
      if (formData.endDate) payload.endDate = new Date(formData.endDate).toISOString();
      if (formData.returnSource) payload.returnSource = formData.returnSource;
      if (formData.returnDestination) payload.returnDestination = formData.returnDestination;
    }
    if (MULTI_STOP_TYPES.has(tripType)) {
      payload.stops = formStops
        .filter((s) => s.location.trim())
        .map((s, i) => ({ location: s.location.trim(), sequence: i + 1 }));
    }
    if (formData.googleMapRoute) payload.googleMapRoute = formData.googleMapRoute;
    if (formData.distance) payload.distance = Number(formData.distance);
    if (formData.eta) payload.eta = formData.eta;
    if (formData.driver) payload.driver = formData.driver;
    if (formData.helper) payload.helper = formData.helper;
    if (formData.vehicle) payload.vehicle = formData.vehicle;
    payload.notifyWhatsApp = formData.notifyWhatsApp !== 'false';
    if (tripType === 'daily_recurring') {
      payload.recurrencePattern = 'daily';
      if (formData.recurrenceEndDate) payload.recurrenceEndDate = new Date(formData.recurrenceEndDate).toISOString();
      if (formData.recurrenceDays) {
        payload.recurrenceDays = formData.recurrenceDays.split(',').map((s) => Number(s.trim())).filter((n) => !Number.isNaN(n));
      }
    }
    if (leadPrefillId && !editId) {
      payload.leadRecordId = leadPrefillId;
      if (formData.leadName) payload.leadName = formData.leadName;
    }
    return payload;
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      const payload = buildPayload();
      if (leadPrefillId && !editId) {
        await tripAPI.fromLead(leadPrefillId, payload);
        showToast('Trip created from lead');
        setLeadPrefillId(null);
      } else if (editId) {
        await tripAPI.update(editId, payload);
        showToast('Trip updated');
      } else {
        await tripAPI.create(payload);
        showToast('Trip created');
      }
      setOpen(false);
      setSuccess(editId ? 'Trip updated' : 'Trip created');
      fetchRows();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (action: () => Promise<unknown>, message: string) => {
    try {
      setSaving(true);
      setError('');
      const res = await action();
      const data = (res as { data?: Record<string, unknown> }).data;
      if (data) setDetailRow(data);
      setSuccess(message);
      showToast(message);
      fetchRows();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async () => {
    if (!detailRow?._id || !assignData.driver) return;
    await runAction(
      () => tripAPI.assign(String(detailRow._id), {
        driver: assignData.driver,
        vehicle: assignData.vehicle || undefined,
        helper: assignData.helper || undefined,
      }),
      'Driver assigned to trip',
    );
    setAssignOpen(false);
  };

  const handleReachStop = async (sequence: number) => {
    if (!detailRow?._id) return;
    await runAction(
      () => tripAPI.reachStop(String(detailRow._id), sequence),
      `Stop ${sequence} marked as reached`,
    );
  };

  const detailStatus = String(detailRow?.status || 'created').toLowerCase();
  const normalizedStatus = detailStatus === 'pending' ? 'created' : detailStatus;

  const detailStops = useMemo(() => {
    const raw = detailRow?.stops as Array<{ location: string; sequence: number; reachedAt?: string }> | undefined;
    return [...(raw || [])].sort((a, b) => a.sequence - b.sequence);
  }, [detailRow]);

  const createFields = tripConfig.fields.filter((f) => !TRIP_FORM_EXCLUDE.has(f.name));

  const handleFormChange = (data: Record<string, string>) => {
    const prevType = formData.tripType;
    const nextType = data.tripType;
    if (prevType !== nextType && !MULTI_STOP_TYPES.has(nextType || '')) {
      setFormStops([]);
    }
    if (nextType === 'round_trip' && data.destination && !data.returnSource) {
      data = { ...data, returnSource: data.destination };
    }
    if (nextType === 'round_trip' && data.source && !data.returnDestination) {
      data = { ...data, returnDestination: data.source };
    }
    setFormData(data);
  };

  useEffect(() => {
    if (!open) return undefined;
    const points = tripRoutePoints(formData, formStops);
    if (points.length < 2) return undefined;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setRouteLoading(true);
      computeDrivingRoute(points)
        .then((route) => {
          if (cancelled || !route) return;
          setFormData((prev) => {
            const nextKm = String(route.km);
            const nextEta = route.eta;
            const nextUrl = route.mapsUrl;
            if (prev.distance === nextKm && prev.eta === nextEta && prev.googleMapRoute === nextUrl) {
              return prev;
            }
            return { ...prev, distance: nextKm, eta: nextEta, googleMapRoute: nextUrl };
          });
        })
        .finally(() => {
          if (!cancelled) setRouteLoading(false);
        });
    }, 450);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // Recalculate when Google locations change, not when distance/eta are filled.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, formData.source, formData.destination, formData.tripType, formData.returnSource, formData.returnDestination, formStops]);

  const TripWorkflowPanel = detailRow ? (
    <Box sx={{ mt: 1 }}>
      <Paper elevation={0} sx={{ p: 2, borderRadius: 2, border: `1px solid ${brandColors.border}`, bgcolor: '#fff', mb: 2 }}>
        <Typography fontWeight={700} sx={{ mb: 1.5, fontSize: 13 }}>Trip progress</Typography>
        <Stepper activeStep={activeStepIndex(normalizedStatus)} alternativeLabel sx={{ mb: 0 }}>
          {WORKFLOW_STEPS.map((step) => (
            <Step key={step} completed={activeStepIndex(normalizedStatus) > WORKFLOW_STEPS.indexOf(step)}>
              <StepLabel>{statusLabel(step)}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25, mb: 2 }}>
        {detailRow.startDate != null ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary">Planned start</Typography>
            <Typography variant="body2" fontWeight={700}>{formatDate(String(detailRow.startDate))}</Typography>
          </Paper>
        ) : null}
        {detailRow.actualStartTime != null ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary">Started</Typography>
            <Typography variant="body2" fontWeight={700}>{formatDate(String(detailRow.actualStartTime))}</Typography>
          </Paper>
        ) : null}
        {detailRow.actualEndTime != null ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary">Finished</Typography>
            <Typography variant="body2" fontWeight={700}>{formatDate(String(detailRow.actualEndTime))}</Typography>
          </Paper>
        ) : null}
        {detailRow.approvedAt != null ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${brandColors.border}`, bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary">Approved</Typography>
            <Typography variant="body2" fontWeight={700}>{formatDate(String(detailRow.approvedAt))}</Typography>
          </Paper>
        ) : null}
      </Box>

      {detailStops.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Stops</Typography>
          <List dense disablePadding>
            {detailStops.map((stop) => (
              <ListItem key={stop.sequence} sx={{ px: 0 }}>
                <ListItemText
                  primary={`${stop.sequence}. ${stop.location}`}
                  secondary={stop.reachedAt ? `Reached ${formatDate(stop.reachedAt)}` : 'Pending'}
                />
                <ListItemSecondaryAction>
                  {stop.reachedAt ? (
                    <Chip size="small" label="Reached" color="success" variant="outlined" />
                  ) : normalizedStatus === 'in_progress' && canEdit ? (
                    <Tooltip title="Mark reached">
                      <IconButton edge="end" size="small" onClick={() => handleReachStop(stop.sequence)}>
                        <PlaceIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ) : null}
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </Box>
      )}

      {String(detailRow.whatsappStatus || '') !== '' && String(detailRow.whatsappStatus) !== 'not_sent' ? (
        <Chip
          size="small"
          icon={<ChatIcon sx={{ fontSize: '14px !important' }} />}
          label={`WhatsApp: ${String(detailRow.whatsappStatus)}`}
          sx={{ mb: 2, bgcolor: brandCss.green100, color: brandCss.primaryDark, fontWeight: 600 }}
        />
      ) : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {normalizedStatus === 'created' && canEdit ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<PersonAddIcon />}
            onClick={() => {
              setAssignData({
                driver: String((detailRow.driver as { _id?: string })?._id || detailRow.driver || ''),
                vehicle: String((detailRow.vehicle as { _id?: string })?._id || detailRow.vehicle || ''),
                helper: String((detailRow.helper as { _id?: string })?._id || detailRow.helper || ''),
              });
              setAssignOpen(true);
            }}
            sx={{ borderRadius: `${RADIUS}px` }}
          >
            Assign Driver
          </Button>
        ) : null}
        {normalizedStatus === 'assigned' && canEdit ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<PlayArrowIcon />}
            disabled={saving}
            onClick={() => runAction(() => tripAPI.start(String(detailRow._id)), 'Trip started')}
            sx={{ borderRadius: `${RADIUS}px`, bgcolor: '#d97706' }}
          >
            Start Trip
          </Button>
        ) : null}
        {normalizedStatus === 'in_progress' && canEdit ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<CheckCircleIcon />}
            disabled={saving}
            onClick={() => runAction(() => tripAPI.complete(String(detailRow._id)), 'Trip completed')}
            sx={{ borderRadius: `${RADIUS}px`, bgcolor: '#2563eb' }}
          >
            Complete Trip
          </Button>
        ) : null}
        {normalizedStatus === 'completed' && canApprove ? (
          <Button
            variant="contained"
            size="small"
            startIcon={<HowToRegIcon />}
            disabled={saving}
            onClick={() => runAction(() => tripAPI.approve(String(detailRow._id)), 'Trip approved')}
            sx={{ borderRadius: `${RADIUS}px` }}
          >
            Approve Trip
          </Button>
        ) : null}
        {detailRow.driver != null && detailRow.driver !== '' && canEdit ? (
          <Button
            variant="outlined"
            size="small"
            startIcon={<ChatIcon />}
            disabled={saving}
            onClick={() => runAction(() => tripAPI.notifyWhatsApp(String(detailRow._id)), 'WhatsApp notification sent')}
            sx={{ borderRadius: `${RADIUS}px`, borderColor: '#25D366', color: '#128C7E' }}
          >
            Send WhatsApp to Driver
          </Button>
        ) : null}
        {['in_progress', 'completed', 'approved'].includes(normalizedStatus) && hasPermission('bilty', 'create') ? (
          <Button
            variant="outlined"
            size="small"
            disabled={saving}
            onClick={() => runAction(() => tripAPI.createBilty(String(detailRow._id)), 'Bilty created from trip')}
            sx={{ borderRadius: `${RADIUS}px` }}
          >
            Create Bilty
          </Button>
        ) : null}
      </Box>
    </Box>
  ) : null;

  return (
    <Box>
      <PageHeader
        title="Trips"
        subtitle={getPageSubtitle('trips', 'Trips', { total: systemTotal })}
        icon={<RouteIcon />}
        action={canCreate ? (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}
            sx={{ px: 3, fontWeight: 700, borderRadius: `${RADIUS}px`, background: brandCss.gradientPrimary }}>
            Add New Trip
          </Button>
        ) : undefined}
      />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: `${RADIUS}px`,
          border: `1px solid ${brandColors.border}`,
          overflow: 'hidden',
          bgcolor: '#fff',
        }}
      >
        <Tabs
          value={tripView}
          onChange={(_, v: TripView) => { setTripView(v); setPage(0); }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 48,
            px: { xs: 0.5, sm: 1 },
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 700,
              fontSize: 13,
              minHeight: 48,
              px: { xs: 1.5, sm: 2 },
            },
          }}
        >
          {TRIP_VIEWS.map((view) => (
            <Tab
              key={view.key}
              value={view.key}
              label={`${view.label} (${viewCounts[view.key]})`}
            />
          ))}
        </Tabs>
      </Paper>

      <DataTable
        columns={tableColumns.columns}
        columnPicker={{
          allColumns: tableColumns.allColumns,
          visibleFields: tableColumns.activeFields,
          defaultFields: tableColumns.defaultFields,
          onSave: tableColumns.updateColumns,
          onReset: tableColumns.resetColumns,
          isCustomized: tableColumns.isCustomized,
        }}
        rows={filteredRows}
        loading={loading}
        search={search}
        onSearchChange={(v) => { setSearch(v); setPage(0); }}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
        onView={(row) => { setDetailRow(row); setDetailOpen(true); }}
        onEdit={canEdit ? openEdit : undefined}
        onDelete={canDelete ? async (row) => {
          if (!window.confirm('Delete this trip?')) return;
          try {
            await tripAPI.remove(String(row._id));
            showToast('Trip deleted');
            fetchRows();
          } catch {
            setError('Delete failed');
          }
        } : undefined}
        onRowClick={(row) => { if (canView) { setDetailRow(row); setDetailOpen(true); } }}
        canView={canView}
        canEdit={canEdit}
        canDelete={canDelete}
        canImport={canImport}
        canExport={canExport}
        enableExcelExport={canExport}
        exportFileName="trips"
        exportSheetName="Trips"
        forceStatusFilter={false}
        statusOptions={[
          { label: 'All Status', value: 'all' },
          { label: 'New', value: 'created' },
          { label: 'Assigned', value: 'assigned' },
          { label: 'Ongoing', value: 'in_progress' },
          { label: 'Completed', value: 'completed' },
          { label: 'Approved', value: 'approved' },
          { label: 'Cancelled', value: 'cancelled' },
        ]}
        onExport={canExport ? async () => {
          try {
            const res = await api.get('/trips/export', { responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement('a'); a.href = url; a.download = 'trips-export.csv'; a.click();
          } catch { setError('Export failed'); }
        } : undefined}
        onImport={canImport ? async (records) => {
          try {
            const res = await api.post('/trips/import', { records });
            setSuccess(`Imported ${res.data.results?.created ?? 0} trip(s)`);
            fetchRows();
          } catch { setError('Import failed'); }
        } : undefined}
        recordLabel="trips"
        emptyMessage={
          tripView === 'ongoing' ? 'No ongoing trips right now'
            : tripView === 'completed' ? 'No completed trips yet'
              : tripView === 'cancelled' ? 'No cancelled trips'
                : 'No trips found'
        }
      />

      <StylishFormModal
        open={open}
        onClose={() => setOpen(false)}
        onSubmit={handleSave}
        title={editId ? 'Edit Trip' : 'Create Trip'}
        subtitle={editId ? 'Update route and planned schedule' : 'Source, destination, and planned date/time'}
        fields={createFields}
        formData={formData}
        onChange={handleFormChange}
        isEdit={Boolean(editId)}
        loading={saving}
        clientOptions={clientOptions}
        vehicleOptions={vehicleOptions}
        branchOptions={branchOptions}
        driverOptions={driverOptions}
        helperOptions={helperOptions}
        extraContent={(fd) => (
          <>
            {(fd.source && fd.destination) ? (
              <Box sx={{
                mb: MULTI_STOP_TYPES.has(fd.tripType) ? 2 : 0,
                px: 2, py: 1.5,
                border: `1px solid ${brandColors.border}`,
                borderRadius: `${RADIUS}px`,
                bgcolor: '#f8fafc',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
              }}>
                <RouteIcon sx={{ color: brandCss.primary, fontSize: 20 }} />
                <Typography variant="body2" fontWeight={700}>
                  {routeLoading ? 'Calculating Google route…' : (fd.distance ? `${fd.distance} km` : 'Select locations for KM')}
                </Typography>
                {fd.eta && !routeLoading && (
                  <Chip size="small" label={`ETA ${fd.eta}`} />
                )}
                {fd.googleMapRoute && (
                  <Button
                    size="small"
                    href={fd.googleMapRoute}
                    target="_blank"
                    rel="noreferrer"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Open in Google Maps
                  </Button>
                )}
              </Box>
            ) : null}
            {MULTI_STOP_TYPES.has(fd.tripType) ? (
              <TripStopsEditor stops={formStops} onChange={setFormStops} />
            ) : null}
          </>
        )}
      />

      <Dialog open={assignOpen} onClose={() => setAssignOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign Trip</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <SearchableEntitySelect
              label="Driver"
              required
              value={assignData.driver}
              options={driverOptions}
              onChange={(v) => setAssignData((d) => ({ ...d, driver: v }))}
              placeholder="Select driver"
            />
            <SearchableEntitySelect
              label="Vehicle (optional)"
              value={assignData.vehicle}
              options={vehicleOptions}
              onChange={(v) => setAssignData((d) => ({ ...d, vehicle: v }))}
              placeholder="Select vehicle"
            />
            <SearchableEntitySelect
              label="Helper (optional)"
              value={assignData.helper}
              options={helperOptions}
              onChange={(v) => setAssignData((d) => ({ ...d, helper: v }))}
              placeholder="Select helper"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={!assignData.driver || saving}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      <RecordDetailDrawer
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        row={detailRow}
        fields={tripConfig.fields}
        moduleTitle="Trip"
        canEdit={canEdit && ['created', 'assigned'].includes(normalizedStatus)}
        canDelete={canDelete && !['in_progress', 'completed', 'approved'].includes(normalizedStatus)}
        onEdit={() => { if (detailRow) { setDetailOpen(false); openEdit(detailRow); } }}
        onDelete={detailRow ? async () => {
          if (!window.confirm('Delete this trip?')) return;
          try {
            await tripAPI.remove(String(detailRow._id));
            setDetailOpen(false);
            showToast('Trip deleted');
            fetchRows();
          } catch {
            setError('Delete failed');
          }
        } : undefined}
        footer={TripWorkflowPanel}
      />
    </Box>
  );
};
